import React, { useState } from 'react';

interface SubjectStats {
  subject: string;
  total: number;
  correct: number;
  incorrect: number;
}

interface QuestionSidebarProps {
  totalQuestions: number;
  currentPage: number;
  questionsPerPage: number;
  currentQuestionIndex: number;
  answeredQuestions: { [key: string]: { correct: boolean; subject?: string } };
  questions: { id: string; subject?: string }[];
  onQuestionSelect: (index: number) => void;
  onPageChange: (page: number) => void;
  isSubscribed: boolean;
  freeQuestionsUsed: number;
  freeQuestionsLimit: number;
  onBookmark?: (questionId: string) => void;
  bookmarkedQuestions?: string[];
  onFontSizeChange?: (size: 'small' | 'medium' | 'large') => void;
  fontSize?: 'small' | 'medium' | 'large';
  onFocusModeToggle?: (enabled: boolean) => void;
  focusMode?: boolean;
  onShowErrorNotebook?: () => void;
  onHighlightToggle?: (enabled: boolean) => void;
  highlightMode?: boolean;
  subjectStats?: SubjectStats[];
  totalAnswered?: number;
  totalCorrect?: number;
}

const QuestionSidebar: React.FC<QuestionSidebarProps> = ({
  totalQuestions,
  currentPage,
  questionsPerPage,
  currentQuestionIndex,
  answeredQuestions,
  questions,
  onQuestionSelect,
  onPageChange,
  isSubscribed,
  freeQuestionsUsed,
  freeQuestionsLimit,
  onBookmark,
  bookmarkedQuestions = [],
  onFontSizeChange,
  fontSize = 'medium',
  onFocusModeToggle,
  focusMode = false,
  onShowErrorNotebook,
  onHighlightToggle,
  highlightMode = false,
  subjectStats = [],
  totalAnswered = 0,
  totalCorrect = 0,
}) => {
  const [showFontSettings, setShowFontSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [activePanel, setActivePanel] = useState<'none' | 'font' | 'settings' | 'stats' | 'errors'>('none');
  
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;

  // Calcular estatísticas da página atual
  const pageStats = {
    total: questions.length,
    answered: questions.filter(q => answeredQuestions[q.id]).length,
    correct: questions.filter(q => answeredQuestions[q.id]?.correct).length,
    incorrect: questions.filter(q => answeredQuestions[q.id] && !answeredQuestions[q.id].correct).length,
  };

  // Calcular questões erradas para o caderno de erros
  const errorQuestions = Object.entries(answeredQuestions)
    .filter(([_, data]) => !data.correct)
    .map(([id]) => id);

  const togglePanel = (panel: 'font' | 'settings' | 'stats' | 'errors') => {
    setActivePanel(activePanel === panel ? 'none' : panel);
  };

  const accuracyRate = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

  return (
    <div className="w-16 bg-white border-l border-gray-200 flex flex-col items-center py-4 gap-2 fixed right-0 top-[73px] h-[calc(100vh-73px)] shadow-lg z-30 transition-all">
      {/* Modo Foco */}
      <button
        onClick={() => onFocusModeToggle?.(!focusMode)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
          focusMode 
            ? 'bg-emerald-500 text-white shadow-md' 
            : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-600'
        }`}
        title={focusMode ? 'Desativar Modo Foco' : 'Ativar Modo Foco'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </button>

      {/* Caderno de Erros */}
      <div className="relative">
        <button
          onClick={() => togglePanel('errors')}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all relative ${
            activePanel === 'errors'
              ? 'bg-red-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600'
          }`}
          title="Caderno de Erros"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {errorQuestions.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {errorQuestions.length > 99 ? '99+' : errorQuestions.length}
            </span>
          )}
        </button>
        
        {activePanel === 'errors' && (
          <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-64 max-h-80 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-gray-900 text-sm">📕 Caderno de Erros</h4>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                {errorQuestions.length}
              </span>
            </div>
            {errorQuestions.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">
                🎉 Nenhum erro ainda! Continue assim!
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">
                  Questões que você errou. Clique para revisar:
                </p>
                <div className="flex flex-wrap gap-1">
                  {errorQuestions.slice(0, 20).map((qId, idx) => (
                    <button
                      key={qId}
                      onClick={() => {
                        const qIndex = questions.findIndex(q => q.id === qId);
                        if (qIndex >= 0) onQuestionSelect(startIndex + qIndex);
                        setActivePanel('none');
                      }}
                      className="w-8 h-8 rounded bg-red-100 text-red-700 text-xs font-bold hover:bg-red-200 transition"
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
                {errorQuestions.length > 20 && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    +{errorQuestions.length - 20} mais...
                  </p>
                )}
                <button
                  onClick={() => {
                    onShowErrorNotebook?.();
                    setActivePanel('none');
                  }}
                  className="w-full mt-3 py-2 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition"
                >
                  Ver Caderno Completo
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bookmark */}
      <button
        onClick={() => questions[currentQuestionIndex - startIndex] && onBookmark?.(questions[currentQuestionIndex - startIndex].id)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
          questions[currentQuestionIndex - startIndex] && bookmarkedQuestions.includes(questions[currentQuestionIndex - startIndex]?.id)
            ? 'bg-amber-500 text-white shadow-md'
            : 'bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-600'
        }`}
        title="Salvar questão"
      >
        <svg className="w-5 h-5" fill={questions[currentQuestionIndex - startIndex] && bookmarkedQuestions.includes(questions[currentQuestionIndex - startIndex]?.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </button>

      {/* Marcador/Highlighter */}
      <button
        onClick={() => onHighlightToggle?.(!highlightMode)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
          highlightMode
            ? 'bg-yellow-500 text-white shadow-md'
            : 'bg-gray-100 text-gray-500 hover:bg-yellow-100 hover:text-yellow-600'
        }`}
        title={highlightMode ? 'Desativar Marcador' : 'Ativar Marcador'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>

      {/* Separador */}
      <div className="w-8 h-px bg-gray-200 my-1" />

      {/* Tamanho da fonte */}
      <div className="relative">
        <button
          onClick={() => togglePanel('font')}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
            activePanel === 'font' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
          title="Tamanho da fonte"
        >
          <span className="text-sm font-bold">Aa</span>
        </button>
        
        {activePanel === 'font' && (
          <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-xl border border-gray-100 p-3 w-36">
            <p className="text-xs font-bold text-gray-400 uppercase mb-2">Tamanho da Fonte</p>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => { onFontSizeChange?.('small'); setActivePanel('none'); }}
                className={`px-3 py-2 rounded-lg text-left transition-all flex items-center gap-2 ${
                  fontSize === 'small' ? 'bg-emerald-100 text-emerald-700 font-bold' : 'hover:bg-gray-100'
                }`}
              >
                <span className="text-xs">A</span>
                <span className="text-xs">Pequena</span>
              </button>
              <button
                onClick={() => { onFontSizeChange?.('medium'); setActivePanel('none'); }}
                className={`px-3 py-2 rounded-lg text-left transition-all flex items-center gap-2 ${
                  fontSize === 'medium' ? 'bg-emerald-100 text-emerald-700 font-bold' : 'hover:bg-gray-100'
                }`}
              >
                <span className="text-sm">A</span>
                <span className="text-xs">Média</span>
              </button>
              <button
                onClick={() => { onFontSizeChange?.('large'); setActivePanel('none'); }}
                className={`px-3 py-2 rounded-lg text-left transition-all flex items-center gap-2 ${
                  fontSize === 'large' ? 'bg-emerald-100 text-emerald-700 font-bold' : 'hover:bg-gray-100'
                }`}
              >
                <span className="text-base">A</span>
                <span className="text-xs">Grande</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Configurações */}
      <div className="relative">
        <button
          onClick={() => togglePanel('settings')}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
            activePanel === 'settings' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
          title="Configurações"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        
        {activePanel === 'settings' && (
          <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-56">
            <h4 className="font-bold text-gray-900 text-sm mb-3">⚙️ Configurações</h4>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-xs text-gray-700">Modo Foco</span>
                <div 
                  onClick={() => onFocusModeToggle?.(!focusMode)}
                  className={`w-10 h-5 rounded-full transition-all cursor-pointer ${focusMode ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-all mt-0.5 ${focusMode ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'}`} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-xs text-gray-700">Marcador de Texto</span>
                <div 
                  onClick={() => onHighlightToggle?.(!highlightMode)}
                  className={`w-10 h-5 rounded-full transition-all cursor-pointer ${highlightMode ? 'bg-yellow-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-all mt-0.5 ${highlightMode ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'}`} />
                </div>
              </label>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Atalhos</p>
                <div className="space-y-1 text-[10px] text-gray-500">
                  <p><kbd className="bg-gray-100 px-1 rounded">F</kbd> Modo Foco</p>
                  <p><kbd className="bg-gray-100 px-1 rounded">H</kbd> Marcador</p>
                  <p><kbd className="bg-gray-100 px-1 rounded">B</kbd> Bookmark</p>
                  <p><kbd className="bg-gray-100 px-1 rounded">→</kbd> Próxima</p>
                  <p><kbd className="bg-gray-100 px-1 rounded">←</kbd> Anterior</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Separador */}
      <div className="w-8 h-px bg-gray-200 my-1" />

      {/* Estatísticas por Matéria */}
      <div className="relative">
        <button
          onClick={() => togglePanel('stats')}
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
            activePanel === 'stats' ? 'bg-blue-500 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600'
          }`}
          title="Estatísticas por Matéria"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </button>
        
        {activePanel === 'stats' && (
          <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-72 max-h-96 overflow-y-auto">
            <h4 className="font-bold text-gray-900 text-sm mb-3">📊 Estatísticas por Matéria</h4>
            
            {/* Resumo Geral */}
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-black text-gray-900">{totalAnswered}</p>
                  <p className="text-[9px] text-gray-500 uppercase">Respondidas</p>
                </div>
                <div>
                  <p className="text-lg font-black text-emerald-600">{totalCorrect}</p>
                  <p className="text-[9px] text-gray-500 uppercase">Acertos</p>
                </div>
                <div>
                  <p className="text-lg font-black text-blue-600">{accuracyRate}%</p>
                  <p className="text-[9px] text-gray-500 uppercase">Taxa</p>
                </div>
              </div>
            </div>
            
            {/* Por Matéria */}
            {subjectStats.length > 0 ? (
              <div className="space-y-2">
                {subjectStats.map((stat, idx) => {
                  const rate = stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0;
                  return (
                    <div key={idx} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold text-gray-700 truncate max-w-[140px]">{stat.subject}</span>
                        <span className={`text-xs font-bold ${rate >= 70 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {rate}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 h-1.5 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${rate >= 70 ? 'bg-emerald-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${rate}%` }}
                          />
                        </div>
                        <span className="text-[9px] text-gray-400">{stat.correct}/{stat.total}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">
                Responda questões para ver estatísticas por matéria
              </p>
            )}
          </div>
        )}
      </div>

      {/* Separador */}
      <div className="w-8 h-px bg-gray-200 my-1" />

      {/* Navegação de questões */}
      <div className="flex flex-col items-center gap-1 flex-1 overflow-y-auto px-1 py-1 scrollbar-thin">
        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Questões</span>
        
        {questions.slice(0, questionsPerPage).map((q, idx) => {
          const globalIndex = startIndex + idx;
          const isAnswered = answeredQuestions[q.id];
          const isCurrent = globalIndex === currentQuestionIndex;
          const isCorrect = isAnswered?.correct;
          const isBookmarked = bookmarkedQuestions.includes(q.id);
          
          let bgColor = 'bg-gray-100 hover:bg-gray-200';
          let textColor = 'text-gray-600';
          let borderColor = '';
          
          if (isCurrent) {
            bgColor = 'bg-emerald-500';
            textColor = 'text-white';
            borderColor = 'ring-2 ring-emerald-300 ring-offset-1';
          } else if (isAnswered) {
            if (isCorrect) {
              bgColor = 'bg-emerald-100';
              textColor = 'text-emerald-700';
            } else {
              bgColor = 'bg-red-100';
              textColor = 'text-red-700';
            }
          }
          
          return (
            <button
              key={q.id}
              onClick={() => onQuestionSelect(globalIndex)}
              className={`w-9 h-9 rounded-lg ${bgColor} ${textColor} ${borderColor} font-bold text-xs transition-all hover:scale-105 relative flex items-center justify-center`}
              title={`Questão ${globalIndex + 1}${isAnswered ? (isCorrect ? ' - Correta' : ' - Incorreta') : ''}`}
            >
              {globalIndex + 1}
              {isBookmarked && (
                <span className="absolute -top-0.5 -right-0.5 text-amber-500 text-[10px]">★</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Estatísticas da página */}
      <div className="w-full px-2 py-2 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <div className="flex justify-center gap-2 mb-1">
            <span className="text-emerald-600 text-[10px] font-bold">{pageStats.correct}✓</span>
            <span className="text-red-500 text-[10px] font-bold">{pageStats.incorrect}✗</span>
          </div>
          <span className="text-[9px] text-gray-500">
            {pageStats.answered}/{pageStats.total}
          </span>
        </div>
      </div>

      {/* Status do usuário */}
      {!isSubscribed && (
        <div className="w-full px-2 pb-2">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-2 text-center border border-amber-200">
            <span className="text-lg font-black text-amber-700 block">
              {freeQuestionsLimit - freeQuestionsUsed}
            </span>
            <span className="text-[9px] text-amber-600 font-medium">restantes</span>
          </div>
        </div>
      )}

      {/* Navegação de páginas */}
      {totalPages > 1 && (
        <div className="w-full px-2 pb-2 border-t border-gray-200 pt-2">
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-7 h-7 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center text-xs"
            >
              ‹
            </button>
            <span className="text-[10px] font-bold text-gray-600 min-w-[40px] text-center">
              {currentPage}/{totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-7 h-7 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center text-xs"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionSidebar;
